<template>
  <div class="card">
    <div class="card-header d-flex justify-content-between align-items-center">
      <h4 class="mb-0">{{ $t("dette.title") }}</h4>
      <a-button
        type="primary"
        icon="plus"
        class="action-button"
        @click="openCreate"
        >{{ $t("dette.newDebt") }}</a-button
      >
    </div>
    <div class="card-body">
      <div class="table-container">
        <a-table
          row-key="_id"
          :loading="loading"
          :columns="columns"
          :data-source="rows"
          :pagination="tablePagination"
          @change="onTableChange"
          :scroll="{ x: 'max-content' }"
          :custom-row="(r) => ({ on: { click: () => openDetail(r) } })"
          :row-class-name="debtsRowClassName"
        >
          <template slot="amount" slot-scope="text">
            <span class="amount-cell">{{
              text != null ? text.toFixed(3) : "—"
            }}</span>
          </template>
          <template slot="status" slot-scope="text, record">
            <a-tag
              :color="
                record._status === 'paid'
                  ? 'green'
                  : record._status === 'partial'
                  ? 'orange'
                  : 'red'
              "
            >
              {{ $t("fournisseur." + (record._status || "unpaid")) }}
            </a-tag>
          </template>
          <template slot="debtType" slot-scope="text">
            {{ debtTypeLabel(text) }}
          </template>
          <template slot="scheduleProgress" slot-scope="text, record">
            <span class="schedule-progress-main">{{
              formatSchedulingProgress(record)
            }}</span>
          </template>
          <template slot="nextPaymentEta" slot-scope="text, record">
            <span
              v-if="formatNextDueEta(record)"
              class="text-muted schedule-eta-cell"
              >{{ formatNextDueEta(record) }}</span
            >
            <span v-else>—</span>
          </template>
          <template slot="action" slot-scope="text, record">
            <a-button
              type="link"
              size="small"
              class="p-0"
              @click.stop="openDetail(record)"
            >
              <a-icon type="edit" />
            </a-button>
          </template>
        </a-table>
      </div>
      <div
        v-if="!loading && rows.length === 0"
        class="text-center text-muted py-4 empty-state"
      >
        {{ $t("dette.noDebts") }}
      </div>
    </div>

    <a-modal
      :visible="createVisible"
      :title="$t('dette.newDebt')"
      :confirm-loading="creating"
      :width="step === 3 ? 920 : 850"
      :footer="null"
      @cancel="createVisible = false"
      destroy-on-close
    >
      <a-steps :current="step" size="small" class="mb-4">
        <a-step :title="$t('dette.debtInfo')" />
        <a-step :title="$t('dette.depenseTemplate')" />
        <a-step :title="$t('dette.installments')" />
        <a-step :title="$t('dette.initialReceiptStep')" />
      </a-steps>

      <a-form :form="debtForm" layout="vertical" v-show="step === 0">
        <div class="row">
          <div class="col-md-6">
            <a-form-item :label="$t('bulletin.label')">
              <a-input
                v-decorator="[
                  'label',
                  {
                    rules: [
                      { required: true, message: $t('requis.designation') },
                    ],
                  },
                ]"
              />
            </a-form-item>
          </div>
          <div class="col-md-6">
            <a-form-item :label="$t('dette.debtType')">
              <a-select
                v-decorator="[
                  'debtType',
                  {
                    rules: [{ required: true, message: $t('requis.required') }],
                  },
                ]"
              >
                <a-select-option value="credit">{{
                  $t("dette.credit")
                }}</a-select-option>
                <a-select-option value="loan">{{
                  $t("dette.loan")
                }}</a-select-option>
                <a-select-option value="engagement">{{
                  $t("dette.engagement")
                }}</a-select-option>
              </a-select>
            </a-form-item>
          </div>
        </div>
        <div class="row">
          <div class="col-md-4">
            <a-form-item :label="$t('dette.beneficiaryType')">
              <a-select
                v-decorator="[
                  'beneficiaryType',
                  {
                    rules: [{ required: true, message: $t('requis.required') }],
                  },
                ]"
                @change="onBenTypeChange"
              >
                <a-select-option value="associate">{{
                  $t("dette.associate")
                }}</a-select-option>
                <a-select-option value="debiteur">{{
                  $t("dette.debiteur")
                }}</a-select-option>
              </a-select>
            </a-form-item>
          </div>
          <div class="col-md-4">
            <a-form-item :label="$t('dette.beneficiary')">
              <a-select
                v-decorator="[
                  'beneficiary',
                  {
                    rules: [{ required: true, message: $t('requis.required') }],
                  },
                ]"
                show-search
                :filter-option="filterBeneficiaryOption"
              >
                <a-select-option
                  v-for="b in beneficiaryOptions"
                  :key="b._id"
                  :value="b._id"
                  :label="beneficiaryOptionLabel(b)"
                >
                  {{ beneficiaryOptionLabel(b) }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </div>
          <div class="col-md-4">
            <a-form-item :label="$t('dette.totalAmount')">
              <a-input-number
                v-decorator="[
                  'totalAmount',
                  {
                    rules: [{ required: true, message: $t('requis.required') }],
                  },
                ]"
                :min="0.001"
                style="width: 100%"
              />
            </a-form-item>
          </div>
        </div>
      </a-form>

      <a-form :form="templateForm" layout="vertical" v-show="step === 1">
        <div class="row">
          <div class="col-md-12">
            <a-form-item :label="$t('comptabilite.reference')">
              <a-input v-decorator="['depenseReference']" />
            </a-form-item>
          </div>
        </div>
        <div class="row">
          <div class="col-md-4">
            <a-form-item :label="$t('comptabilite.tabUnites')">
              <a-select
                v-decorator="['unit']"
                allow-clear
                show-search
                option-filter-prop="children"
              >
                <a-select-option
                  v-for="u in lookups.units"
                  :key="u._id"
                  :value="u._id"
                  >{{ u.name }}</a-select-option
                >
              </a-select>
            </a-form-item>
          </div>
          <div class="col-md-8">
            <a-form-item :label="$t('comptabilite.tabDepenseSubcategories')">
              <a-select
                v-decorator="[
                  'subCategory',
                  {
                    rules: [{ required: true, message: $t('requis.required') }],
                  },
                ]"
                show-search
                :filter-option="subcategoryFilterOption"
              >
                <a-select-opt-group
                  v-for="g in subcategoryGroups"
                  :key="g.categoryId"
                >
                  <span slot="label" class="depense-subcategory-group-label">
                    <span class="depense-subcategory-group-name">{{
                      g.categoryName
                    }}</span>
                    <a-tag
                      v-if="g.expenseKind"
                      :color="expenseKindTagColor(g.expenseKind)"
                      class="depense-subcategory-expense-kind-tag"
                    >
                      {{ $t(expenseKindTagLabelKey(g.expenseKind)) }}
                    </a-tag>
                  </span>
                  <a-select-option
                    v-for="s in g.subs"
                    :key="s._id"
                    :value="s._id"
                  >
                    {{ s.name }}
                  </a-select-option>
                </a-select-opt-group>
              </a-select>
            </a-form-item>
          </div>
        </div>
        <div class="row">
          <div class="col-md-4">
            <a-form-item :label="$t('dette.numberOfInstallments')">
              <a-input-number
                v-decorator="[
                  'nbInstallments',
                  {
                    rules: [{ required: true, message: $t('requis.required') }],
                  },
                ]"
                :min="1"
                :max="120"
                style="width: 100%"
              />
            </a-form-item>
          </div>
          <div class="col-md-4">
            <a-form-item :label="$t('dette.firstInstallmentDate')">
              <a-date-picker
                v-decorator="[
                  'firstDate',
                  { rules: [{ required: true, message: $t('requis.date') }] },
                ]"
                format="DD/MM/YYYY"
                style="width: 100%"
              />
            </a-form-item>
          </div>
        </div>
      </a-form>

      <div v-show="step === 2">
        <a-table
          row-key="idx"
          :data-source="installments"
          :pagination="false"
          size="small"
        >
          <a-table-column :title="'#'" key="idx">
            <template slot-scope="text, record">{{ record.idx + 1 }}</template>
          </a-table-column>
          <a-table-column :title="$t('dette.installmentAmount')" key="amount">
            <template slot-scope="text, record">
              <a-input-number
                v-model="record.amount"
                :min="0.001"
                :step="0.001"
                size="small"
                style="width: 120px"
              />
            </template>
          </a-table-column>
          <a-table-column :title="$t('fournisseur.dueDate')" key="date">
            <template slot-scope="text, record">
              <a-date-picker
                v-model="record.date"
                format="DD/MM/YYYY"
                size="small"
              />
            </template>
          </a-table-column>
        </a-table>
        <div class="mt-3 d-flex justify-content-between align-items-center">
          <span :class="{ 'text-danger': !sumMatch, 'text-success': sumMatch }">
            {{ $t("dette.totalAmount") }}: {{ totalAmount.toFixed(3) }} |
            {{ $t("dette.installments") }}: {{ installmentSum.toFixed(3) }}
            <template v-if="!sumMatch">
              — {{ $t("dette.sumMismatch") }}</template
            >
          </span>
        </div>
      </div>

      <div v-show="step === 3">
        <debt-initial-receipt-step
          ref="debtReceiptStep"
          :total-amount="totalAmount"
          :lookups="caisseLookups"
        />
      </div>

      <div class="mt-4 d-flex justify-content-end flex-wrap" style="gap: 8px">
        <a-button v-if="step > 0" @click="step--">{{
          $t("action.retour")
        }}</a-button>
        <a-button
          v-if="step < 3"
          type="primary"
          :disabled="step === 2 && !sumMatch"
          @click="nextStep"
          >{{ $t("action.suivant") || "Next" }}</a-button
        >
        <a-button
          v-if="step === 3"
          type="primary"
          :loading="creating"
          @click="submitCreate"
        >
          {{ $t("action.enregistrer") }}
        </a-button>
      </div>
    </a-modal>

    <a-drawer
      :visible="detailVisible"
      :title="$t('dette.debtDetail')"
      :width="initialEncaissementPayment ? 920 : 780"
      @close="detailVisible = false"
      destroy-on-close
    >
      <template v-if="detailData">
        <a-descriptions bordered size="small" :column="2" class="mb-3">
          <a-descriptions-item :label="$t('bulletin.label')">{{
            detailData.debt.label
          }}</a-descriptions-item>
          <a-descriptions-item :label="$t('dette.debtType')">{{
            debtTypeLabel(detailData.debt.debtType)
          }}</a-descriptions-item>
          <a-descriptions-item :label="$t('dette.totalAmount')">{{
            detailData.debt.totalAmount.toFixed(3)
          }}</a-descriptions-item>
          <a-descriptions-item :label="$t('dette.beneficiaryType')">{{
            debtDetailBeneficiaryTypeLabel(detailData.debt)
          }}</a-descriptions-item>
          <a-descriptions-item :label="$t('dette.beneficiary')">{{
            detailData.debt.beneficiaryName || "—"
          }}</a-descriptions-item>
        </a-descriptions>

        <template v-if="initialEncaissementPayment">
          <a-divider orientation="left">{{
            $t("dette.initialReceiptDetail")
          }}</a-divider>
          <a-descriptions bordered size="small" :column="1" class="mb-3">
            <a-descriptions-item :label="$t('recette.montant')">
              <span class="amount-cell">{{
                (initialEncaissementPayment.totalReceived || 0).toFixed(3)
              }}</span>
            </a-descriptions-item>
            <a-descriptions-item
              v-if="initialEncaissementPayment.receiptNumber"
              :label="$t('decaissement.receiptNumber')"
            >
              {{ initialEncaissementPayment.receiptNumber }}
            </a-descriptions-item>
            <a-descriptions-item :label="$t('dette.receivedAt')">
              {{
                formatDateTime(
                  initialEncaissementPayment.receivedAt ||
                    initialEncaissementPayment.createdAt
                )
              }}
            </a-descriptions-item>
            <a-descriptions-item :label="$t('dette.receivedBy')">
              {{
                initialEncaissementPayment.receivedBy ||
                initialEncaissementPayment.createdBy ||
                "—"
              }}
            </a-descriptions-item>
            <a-descriptions-item
              v-if="
                initialEncaissementPayment.methods &&
                initialEncaissementPayment.methods.length
              "
              :label="$t('paiementHistory.method')"
            >
              <a-tag
                v-for="m in initialEncaissementPayment.methods"
                :key="m"
                class="mr-1"
              >
                <a-icon :type="methodIconEnc(m)" class="mr-1" />{{
                  methodLabelEnc(m)
                }}
              </a-tag>
            </a-descriptions-item>
          </a-descriptions>

          <div class="text-muted small mb-2">
            {{ $t("dette.encaissementLines") }}
          </div>
          <div
            v-for="(sp, spIdx) in initialReceiptSplitsEnriched"
            :key="'sp-' + spIdx"
            class="debt-enc-split-detail mb-3"
          >
            <div class="debt-enc-split-title">
              {{ $t("saisiePaiement.paymentLine", { n: spIdx + 1 }) }}
              <template
                v-if="
                  (initialEncaissementPayment.splitPayments || []).length === 1
                "
              >
                — <a-icon :type="methodIconEnc(sp.method)" class="mr-1" />
                {{ methodLabelEnc(sp.method) }}
              </template>
            </div>
            <a-descriptions bordered :column="1" size="small">
              <a-descriptions-item
                v-if="
                  (initialEncaissementPayment.splitPayments || []).length > 1
                "
                :label="$t('paiementHistory.method')"
              >
                <a-icon :type="methodIconEnc(sp.method)" class="mr-1" />
                {{ methodLabelEnc(sp.method) }}
              </a-descriptions-item>
              <a-descriptions-item :label="$t('recette.montant')">
                <span class="amount-cell">{{
                  (sp.amount != null ? sp.amount : 0).toFixed(3)
                }}</span>
              </a-descriptions-item>
              <a-descriptions-item
                v-if="sp.caisseId && sp._caisseName"
                :label="$t('decaissement.cashName')"
              >
                {{ sp._caisseName }}
              </a-descriptions-item>
              <a-descriptions-item
                v-if="sp.caisseBankId && sp._bankName"
                :label="$t('decaissement.caisseBank')"
              >
                {{ sp._bankName }}
              </a-descriptions-item>
            </a-descriptions>

            <template
              v-if="
                sp.methodDetails &&
                sp.methodDetails.cheques &&
                sp.methodDetails.cheques.length
              "
            >
              <a-divider orientation="left"
                ><a-icon type="credit-card" />
                {{ $t("decaissement.paymentMethod.cheque") }}</a-divider
              >
              <a-table
                :columns="chequeEncColumns"
                :data-source="sp.methodDetails.cheques"
                :pagination="false"
                size="small"
                :row-key="(r, i) => 'ch-' + spIdx + '-' + i"
                bordered
              >
                <template slot="montant" slot-scope="text"
                  ><span class="amount-cell">{{
                    (text || 0).toFixed(3)
                  }}</span></template
                >
              </a-table>
            </template>
            <template
              v-if="
                sp.methodDetails &&
                sp.methodDetails.traites &&
                sp.methodDetails.traites.length
              "
            >
              <a-divider orientation="left"
                ><a-icon type="file-protect" />
                {{ $t("decaissement.paymentMethod.traite") }}</a-divider
              >
              <a-table
                :columns="traiteEncColumns"
                :data-source="sp.methodDetails.traites"
                :pagination="false"
                size="small"
                :row-key="(r, i) => 'tr-' + spIdx + '-' + i"
                bordered
              >
                <template slot="montant" slot-scope="text"
                  ><span class="amount-cell">{{
                    (text || 0).toFixed(3)
                  }}</span></template
                >
              </a-table>
            </template>
            <template
              v-if="
                sp.methodDetails &&
                sp.methodDetails.virements &&
                sp.methodDetails.virements.length
              "
            >
              <a-divider orientation="left"
                ><a-icon type="bank" />
                {{ $t("decaissement.paymentMethod.virement") }}</a-divider
              >
              <a-table
                :columns="virementEncColumns"
                :data-source="sp.methodDetails.virements"
                :pagination="false"
                size="small"
                :row-key="(r, i) => 'vi-' + spIdx + '-' + i"
                bordered
              >
                <template slot="montant" slot-scope="text"
                  ><span class="amount-cell">{{
                    (text || 0).toFixed(3)
                  }}</span></template
                >
              </a-table>
            </template>
          </div>
        </template>

        <a-divider orientation="left">{{ $t("dette.installments") }}</a-divider>
        <template v-if="depensesForPanels.length">
          <depense-schedules-panel
            v-for="(dep, idx) in depensesForPanels"
            :key="String(dep._id)"
            :class="{ 'debt-schedule-panel--spaced': idx > 0 }"
            :depense="dep"
            :show-section-title="depensesForPanels.length > 1"
            hide-payment-flow
            @refresh="onDebtSchedulesRefresh"
          />
        </template>
        <p v-else class="text-muted">{{ $t("dette.noSchedulesDetail") }}</p>
      </template>
    </a-drawer>
  </div>
</template>

<script>
import moment from "moment";
import {
  fetchDebts,
  createDebt,
  fetchDebtDetail,
} from "@/services/api/compV3/paymentEntry";
import { fetchDecaissementLookups } from "@/services/api/compV3/decaissement";
import apiClient from "@/services/axios";
import { showApiError } from "@/utilities/showApiError";
import {
  buildSubcategoryGroups,
  categoryIdForSubcategory,
  createSubcategoryFilter,
} from "@/utilities/depense/depenseSubcategories";
import {
  expenseKindTagColor,
  expenseKindTagLabelKey,
} from "@/utilities/depense/expenseKindUi";
import DepenseSchedulesPanel from "../suppliers/components/DepenseSchedulesPanel.vue";
import DebtInitialReceiptStep from "./components/DebtInitialReceiptStep.vue";

export default {
  name: "DebtPage",
  components: { DepenseSchedulesPanel, DebtInitialReceiptStep },
  beforeMount() {
    this.debtForm = this.$form.createForm(this, { name: "debtForm" });
    this.templateForm = this.$form.createForm(this, { name: "templateForm" });
  },
  data() {
    return {
      rows: [],
      loading: false,
      pagination: { current: 1, pageSize: 20, total: 0 },
      lookups: {
        units: [],
        categories: [],
        subcategories: [],
        associates: [],
        debiteurs: [],
        paymentSources: {},
      },
      createVisible: false,
      creating: false,
      step: 0,
      installments: [],
      totalAmount: 0,
      benType: null,
      detailVisible: false,
      detailData: null,
    };
  },
  computed: {
    beneficiaryOptions() {
      if (this.benType === "associate") return this.lookups.associates || [];
      if (this.benType === "debiteur") return this.lookups.debiteurs || [];
      return [];
    },
    installmentSum() {
      return this.installments.reduce((s, i) => s + (Number(i.amount) || 0), 0);
    },
    sumMatch() {
      return Math.abs(this.installmentSum - this.totalAmount) < 0.01;
    },
    tablePagination() {
      return {
        current: this.pagination.current,
        pageSize: this.pagination.pageSize,
        total: this.pagination.total,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50"],
        showTotal: (t) => `${t}`,
      };
    },
    subcategoryGroups() {
      return buildSubcategoryGroups(
        this.lookups.categories || [],
        this.lookups.subcategories || []
      );
    },
    subcategoryFilterOption() {
      return createSubcategoryFilter(
        this.lookups.categories || [],
        this.lookups.subcategories || []
      );
    },
    columns() {
      return [
        {
          title: this.$t("bulletin.label"),
          dataIndex: "label",
          key: "label",
          ellipsis: true,
        },
        {
          title: this.$t("dette.debtType"),
          dataIndex: "debtType",
          key: "type",
          scopedSlots: { customRender: "debtType" },
        },
        {
          title: this.$t("dette.totalAmount"),
          dataIndex: "totalAmount",
          key: "total",
          scopedSlots: { customRender: "amount" },
        },
        {
          title: this.$t("fournisseur.scheduleProgress"),
          key: "scheduleProgress",
          scopedSlots: { customRender: "scheduleProgress" },
        },
        {
          title: this.$t("dette.nextPaymentEta"),
          key: "nextPaymentEta",
          scopedSlots: { customRender: "nextPaymentEta" },
        },
        {
          title: this.$t("action.status"),
          key: "status",
          scopedSlots: { customRender: "status" },
        },
        {
          title: this.$t("action.actions"),
          key: "action",
          scopedSlots: { customRender: "action" },
        },
      ];
    },
    /** Depense objects for schedule batch editor (normalized total). */
    depensesForPanels() {
      if (!this.detailData || !Array.isArray(this.detailData.depenses))
        return [];
      return this.detailData.depenses.map((d) => ({
        ...d,
        _totalAmount: Number(
          d._totalAmount ?? d.amountTTC ?? d.amount ?? d.money ?? 0
        ),
      }));
    },
    /** Aligns with Avoir receive modal / lookups.bootstrap paymentSources */
    caisseLookups() {
      const ps = this.lookups.paymentSources || {};
      return {
        myCaisses: ps.myCaisses || (ps.myCaisse ? [ps.myCaisse] : []),
        otherCaisses: ps.otherCaisses || ps.depenseCashCaisses || [],
        caisseBanks: ps.caisseBanks || [],
      };
    },
    /** Populated encaissement doc linked from debt (GET /debts/:id). */
    initialEncaissementPayment() {
      const d = this.detailData && this.detailData.debt;
      if (!d || !d.initialEncaissementPayment) return null;
      const p = d.initialEncaissementPayment;
      if (
        typeof p === "object" &&
        p !== null &&
        (Array.isArray(p.splitPayments) || p.totalReceived != null)
      ) {
        return p;
      }
      return null;
    },
    initialReceiptSplitsEnriched() {
      const p = this.initialEncaissementPayment;
      if (!p || !Array.isArray(p.splitPayments)) return [];
      const ps = this.lookups.paymentSources || {};
      const cashList = [];
      const seen = new Set();
      const addC = (c) => {
        if (!c || !c._id) return;
        const id = String(c._id);
        if (seen.has(id)) return;
        seen.add(id);
        cashList.push(c);
      };
      addC(ps.myCaisse);
      (ps.otherCaisses || []).forEach(addC);
      (ps.depenseCashCaisses || []).forEach(addC);
      const banks = ps.caisseBanks || [];
      return p.splitPayments.map((sp) => {
        let _caisseName = "";
        let _bankName = "";
        if (sp.caisseId) {
          const c = cashList.find((x) => String(x._id) === String(sp.caisseId));
          _caisseName = c ? c.name || c.label || String(c._id) : "";
        }
        if (sp.caisseBankId) {
          const b = banks.find(
            (x) => String(x._id) === String(sp.caisseBankId)
          );
          _bankName = b ? b.name || b.label || String(b._id) : "";
        }
        return { ...sp, _caisseName, _bankName };
      });
    },
    chequeEncColumns() {
      const fmtDue = (text) => this.formatDate(text);
      return [
        {
          title: this.$t("decaissement.chequeNum"),
          dataIndex: "num",
          key: "num",
        },
        {
          title: this.$t("fournisseur.dueDate"),
          dataIndex: "dateEchance",
          key: "date",
          customRender: fmtDue,
        },
        {
          title: this.$t("decaissement.chequeTitulaire"),
          dataIndex: "titulaire",
          key: "tit",
        },
        {
          title: this.$t("recette.montant"),
          dataIndex: "montant",
          key: "mt",
          scopedSlots: { customRender: "montant" },
        },
      ];
    },
    traiteEncColumns() {
      const fmtDue = (text) => this.formatDate(text);
      return [
        {
          title: this.$t("decaissement.chequeNum"),
          dataIndex: "num",
          key: "num",
        },
        {
          title: this.$t("fournisseur.dueDate"),
          dataIndex: "dateEchance",
          key: "date",
          customRender: fmtDue,
        },
        {
          title: this.$t("decaissement.traiteRib"),
          dataIndex: "rib",
          key: "rib",
        },
        {
          title: this.$t("recette.montant"),
          dataIndex: "montant",
          key: "mt",
          scopedSlots: { customRender: "montant" },
        },
      ];
    },
    virementEncColumns() {
      return [
        {
          title: this.$t("decaissement.virementNum"),
          dataIndex: "num",
          key: "num",
        },
        {
          title: this.$t("decaissement.chequeTitulaire"),
          dataIndex: "titulaire",
          key: "tit",
        },
        {
          title: this.$t("recette.montant"),
          dataIndex: "montant",
          key: "mt",
          scopedSlots: { customRender: "montant" },
        },
      ];
    },
  },
  async created() {
    await this.loadAll();
  },
  methods: {
    expenseKindTagColor,
    expenseKindTagLabelKey,
    debtsRowClassName(record, index) {
      return index % 2 === 0 ? "table-row-even" : "table-row-odd";
    },
    formatDate(v) {
      return v ? moment(v).format("DD/MM/YYYY") : "—";
    },
    formatDateTime(v) {
      return v ? moment(v).format("DD/MM/YYYY HH:mm") : "—";
    },
    debtDetailBeneficiaryTypeLabel(debt) {
      if (!debt) return "—";
      const bt = debt.beneficiaryType;
      if (bt === "associate") return this.$t("dette.associate");
      if (bt === "debiteur") return this.$t("dette.debiteur");
      return "—";
    },
    debtTypeLabel(t) {
      const key = "dette." + t;
      const tr = this.$t(key);
      return tr !== key ? tr : t || "—";
    },
    methodLabelEnc(m) {
      const key = "decaissement.paymentMethod." + m;
      const t = this.$t(key);
      return t !== key ? t : m || "—";
    },
    methodIconEnc(m) {
      const map = {
        cash: "wallet",
        cheque: "credit-card",
        virement: "bank",
        traite: "file-protect",
        prelevement: "sync",
        tpe: "tablet",
      };
      return map[m] || "dollar";
    },
    formatSchedulingProgress(record) {
      const stats =
        record && record._scheduleStats ? record._scheduleStats : {};
      const paid = Number(stats.paid) || 0;
      const total = Number(stats.total) || 0;
      if (!total) return "0/0";
      return `${paid}/${total}`;
    },
    formatNextDueEta(record) {
      const stats =
        record && record._scheduleStats ? record._scheduleStats : {};
      if (!stats.nextPendingDueDate) return "";
      const now = moment().startOf("day");
      const due = moment(stats.nextPendingDueDate).startOf("day");
      const days = due.diff(now, "days");
      if (days < 0)
        return this.$t("fournisseur.scheduleOverdueByDays", {
          n: Math.abs(days),
        });
      if (days === 0) return this.$t("fournisseur.scheduleDueToday");
      if (days < 30)
        return this.$t("fournisseur.scheduleDueInDays", { n: days });
      const months = due.diff(now, "months");
      return this.$t("fournisseur.scheduleDueInMonths", {
        n: Math.max(1, months),
      });
    },
    async loadAll() {
      this.loading = true;
      try {
        const [debtsRes, lookupsRes, debiteursRes] = await Promise.all([
          fetchDebts({
            page: this.pagination.current,
            pageSize: this.pagination.pageSize,
          }),
          fetchDecaissementLookups({ forEncaissement: true }),
          apiClient.get("/compV3/depense/debiteurs"),
        ]);
        const body = debtsRes.data || {};
        this.rows = body.data || [];
        this.pagination.total = body.total || 0;
        this.pagination.current = body.page || 1;
        const ld = lookupsRes.data || {};
        this.lookups = {
          units: ld.units || [],
          categories: ld.categories || [],
          subcategories: ld.subcategories || [],
          associates: ld.associates || [],
          debiteurs: debiteursRes.data || [],
          paymentSources: ld.paymentSources || {},
        };
      } catch {
        this.$message.error(this.$t("warning.connectionProblem"));
      } finally {
        this.loading = false;
      }
    },
    onTableChange(pag) {
      this.pagination.current = pag.current;
      this.pagination.pageSize = pag.pageSize;
      this.loadAll();
    },
    onBenTypeChange(val) {
      this.benType = val;
      this.$nextTick(() => {
        this.debtForm.setFieldsValue({ beneficiary: undefined });
      });
    },
    beneficiaryOptionLabel(b) {
      if (!b) return "";
      return b.fullName || b.name || b.reference || String(b._id || "");
    },
    filterBeneficiaryOption(input, option) {
      const id = option.key;
      const opt = this.beneficiaryOptions.find(
        (b) => String(b._id) === String(id)
      );
      const label = opt ? this.beneficiaryOptionLabel(opt) : "";
      return (
        !input || label.toLowerCase().includes(String(input).toLowerCase())
      );
    },
    openCreate() {
      this.step = 0;
      this.installments = [];
      this.totalAmount = 0;
      this.benType = null;
      this.createVisible = true;
    },
    nextStep() {
      if (this.step === 0) {
        this.debtForm.validateFields((err, values) => {
          if (err) return;
          this.totalAmount = values.totalAmount;
          this.step = 1;
        });
      } else if (this.step === 1) {
        this.templateForm.validateFields((err, values) => {
          if (err) return;
          this.generateInstallments(values);
          this.step = 2;
        });
      } else if (this.step === 2) {
        if (!this.sumMatch) return;
        this.step = 3;
        this.$nextTick(() => {
          if (
            this.$refs.debtReceiptStep &&
            this.$refs.debtReceiptStep.resetLines
          ) {
            this.$refs.debtReceiptStep.resetLines();
          }
        });
      }
    },
    generateInstallments(values) {
      const n = values.nbInstallments;
      const perInstallment = Number((this.totalAmount / n).toFixed(3));
      const firstDate = values.firstDate;
      this.installments = [];
      for (let i = 0; i < n; i++) {
        const d = moment(firstDate).add(i, "months");
        const amt =
          i === n - 1
            ? Number((this.totalAmount - perInstallment * (n - 1)).toFixed(3))
            : perInstallment;
        this.installments.push({ idx: i, amount: amt, date: d });
      }
    },
    async submitCreate() {
      if (this.step !== 3) return;
      if (!this.sumMatch) return;
      const receipt =
        this.$refs.debtReceiptStep && this.$refs.debtReceiptStep.buildPayload();
      if (!receipt) return;
      this.creating = true;
      try {
        const debtVals = this.debtForm.getFieldsValue();
        const templateVals = this.templateForm.getFieldsValue();
        const payload = {
          label: debtVals.label,
          debtType: debtVals.debtType,
          beneficiaryType: debtVals.beneficiaryType,
          beneficiary: debtVals.beneficiary,
          totalAmount: debtVals.totalAmount,
          depenseReference: templateVals.depenseReference || "",
          unit: templateVals.unit || undefined,
          category: categoryIdForSubcategory(
            this.lookups.subcategories,
            templateVals.subCategory
          ),
          subCategory: templateVals.subCategory,
          installments: this.installments.map((i) => ({
            amount: i.amount,
            date: i.date && i.date.toDate ? i.date.toDate() : i.date,
          })),
          initialReceipt: {
            splits: receipt.splits,
            receiptNumber: receipt.receiptNumber,
          },
        };
        await createDebt(payload);
        this.$message.success(this.$t("dette.debtCreated"));
        this.createVisible = false;
        await this.loadAll();
      } catch (ex) {
        showApiError(this, ex);
      } finally {
        this.creating = false;
      }
    },
    async openDetail(record) {
      try {
        const ps = this.lookups.paymentSources || {};
        if (
          !(ps.myCaisses || []).length &&
          !(ps.otherCaisses || []).length &&
          !(ps.depenseCashCaisses || []).length &&
          !(ps.caisseBanks || []).length
        ) {
          const lookupsRes = await fetchDecaissementLookups({
            forEncaissement: true,
          });
          const ld = lookupsRes.data || {};
          this.lookups.paymentSources = ld.paymentSources || {};
        }
        const res = await fetchDebtDetail(record._id);
        this.detailData = res.data;
        this.detailVisible = true;
      } catch {
        this.$message.error(this.$t("warning.connectionProblem"));
      }
    },
    async onDebtSchedulesRefresh() {
      const id =
        this.detailData && this.detailData.debt && this.detailData.debt._id;
      if (!id) return;
      try {
        const res = await fetchDebtDetail(id);
        this.detailData = res.data;
        await this.loadAll();
      } catch {
        this.$message.error(this.$t("warning.connectionProblem"));
      }
    },
  },
};
</script>

<style scoped>
.ant-table-tbody tr {
  cursor: pointer;
}
.schedule-progress-main {
  font-weight: 600;
}
.schedule-eta-cell {
  font-size: 12px;
}
.debt-schedule-panel--spaced {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px dashed #e8e8e8;
}
.debt-enc-split-title {
  font-weight: 600;
  margin-bottom: 8px;
  color: #262626;
}
.debt-enc-split-detail {
  padding-bottom: 8px;
  border-bottom: 1px dashed #f0f0f0;
}
.debt-enc-split-detail:last-of-type {
  border-bottom: none;
}
</style>

<style>
@import "~@/views/compV3/common.css";
</style>
